// What a signals write hands back, and the shape the card draws from.
//
// rgip's `presentation.py` puts this on the `activity_end` of `springbok_add_signals`,
// nested under `receipt.card`, exactly as a tree write and a score lookup do. The reason
// is the same: a write lands several sentences across one or more groups, each with a
// direction and an order that decides which one a lead is actually told, and the agent
// was listing all of that in prose.
//
// The sentence and its direction arrive APART, because the card draws them in different
// places. The condition arrives as the stored SQL and goes through `unparseConditionToUi`
// — the parser Studio's own signal editor reads a rule back with — so the card and the
// Signals tab cannot describe one rule two ways.
/** One written rule: what a rep reads, which way it argues, and what fires it. */
export interface SignalCardRule {
  says?: string;
  argues_for?: boolean;
  /** As STORED, for `unparseConditionToUi`. Never pre-rendered English. */
  condition?: string;
  priority?: number;
}

/** One group, which compiles to a single CASE: first match wins. */
export interface SignalCardGroup {
  group?: string;
  column?: string;
  /**
   * Whether the order between these rules changes what a lead is told.
   *
   * True only on a column that can hold several values at once, where a lead can satisfy
   * more than one rule and the first listed wins. Drawn, because a plain list of rules
   * implies the order never matters.
   */
  order_decides_the_outcome?: boolean;
  rules: SignalCardRule[];
  /**
   * Sentences this rewrite took OUT, as they read before.
   *
   * A group edited from six rules down to three is mostly a story about the three that
   * went, and nothing else on screen says they were ever there. Only a rewrite has these;
   * a new group has nothing to have removed.
   */
  dropped?: string[];
}

export interface SignalCardData {
  kind: 'signals';
  groups: SignalCardGroup[];
  priority?: number;
  /** Every rule on the model after this write, not just the new ones. */
  signals_total?: number;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * The signals card a receipt carries, or null when it carries none.
 *
 * Null is ordinary: every other tool's receipt has no signals card, and a PREVIEW sends
 * none, because a card for signals that do not exist yet reads as a receipt for them.
 */
export function signalCardFrom(receipt: unknown): SignalCardData | null {
  if (!isObject(receipt)) return null;
  const card = receipt.card;
  if (!isObject(card) || card.kind !== 'signals') return null;
  if (!Array.isArray(card.groups) || !card.groups.length) return null;
  return card as unknown as SignalCardData;
}

// NOTE for anyone reaching for `unparseConditionToUi`: its `direction` argument is the
// LOGICAL joiner, 'AND' | 'OR', not the signal's plus or minus. A rule written here is a
// single condition, so it is always 'AND'; the signal's side is `argues_for` and the two
// are unrelated despite both being called a direction.

/** How many rules this card wrote, for a heading that says what it is. */
export function ruleCount(card: SignalCardData): number {
  return card.groups.reduce((total, group) => total + (group.rules?.length ?? 0), 0);
}
